import React from 'react';
import { Button, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';

import {
  VIDEO_PRODUCTION_ADAPT_TYPE_OPTIONS,
  VIDEO_PRODUCTION_DEFAULT_ADAPT_TYPE,
  VIDEO_PRODUCTION_SLOT_KIND,
  VIDEO_TEXT_FIT_RUNTIME_LIMITS,
} from '@/features/automation/videoEditing/constants.js';
import { getTemplateSlotKindLabel } from '@/features/automation/videoEditing/utils/templateSlotUtils.js';
import { isMediaSlotKind } from '@/features/automation/videoEditing/utils/productionSlotUtils.js';
import {
  SortableDragHandle,
  useVerticalSortableItem,
} from '@/components/sortable/VerticalSortableList.js';

const EMPTY_VALUE_LABEL = '-';
const DEFAULT_FONT_OPTION_LABEL = '模板默认';
const FONT_LABEL = '字体';
const SLOT_COUNT_ARIA_SUFFIX = '个插槽';
const GROUP_NAME_PLACEHOLDER = '组名';
const MAX_LENGTH_LABEL = '最大数量';
const TEXT_GROUP_MAX_LENGTH_TOOLTIP =
  '文字组中每个按换行分配的段落允许输入的最大字数';
const TEXT_SLOT_MAX_LENGTH_TOOLTIP = '该插槽允许输入的最大字数';
const SLOT_SELECT_ARIA_PREFIX = '选择插槽';
const GROUP_SELECT_ARIA_PREFIX = '选择分组';
const GROUP_SORT_ARIA_PREFIX = '拖动排序';
const SLOT_SORT_ARIA_PREFIX = '拖动排序';
const MIN_POSITIVE_INTEGER = 1;
const TOOLTIP_PLACEMENT = 'top';
const TEXT_FIT_RUNTIME_FIELDS = [
  {
    key: 'reference_size',
    label: '字号',
    tooltip: '用于判断文字是否溢出的参考字号',
  },
  {
    key: 'max_chars_per_line',
    label: '字数',
    tooltip: '参考字号下单个插槽每行最多容纳的字数',
  },
  {
    key: 'max_lines',
    label: '行数',
    tooltip: '单个真实插槽最多行数；文字组按组内每个插槽分别计算',
  },
];

const joinClassNames = (...classNames) => classNames.filter(Boolean).join(' ');

const RuntimeFieldLabel = ({ id, label, tooltip }) => (
  <Form.Label>
    <OverlayTrigger
      placement={TOOLTIP_PLACEMENT}
      trigger={['hover', 'focus']}
      overlay={<Tooltip id={`${id}-tooltip`}>{tooltip}</Tooltip>}
    >
      <span
        className="slot-text-runtime-label"
        tabIndex={0}
        aria-label={`${label}：${tooltip}`}
      >
        {label}
      </span>
    </OverlayTrigger>
  </Form.Label>
);

const FontSelectControl = ({ disabled, fontOptions, onChange, value }) => {
  const options = [...fontOptions];
  if (value && !options.some((option) => option.value === value)) {
    options.push({ value, label: value });
  }

  return (
    <div className="slot-font-control">
      <Form.Label>{FONT_LABEL}</Form.Label>
      <Form.Select
        className="slot-config-select slot-font-select"
        value={value}
        onChange={onChange}
        disabled={disabled}
        aria-label={FONT_LABEL}
      >
        <option value="">{DEFAULT_FONT_OPTION_LABEL}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Form.Select>
    </div>
  );
};

const TextRuntimeControls = ({
  disabled,
  maxLengthTooltip,
  onChange,
  runtime,
  tooltipIdPrefix,
}) => (
  <div className="slot-text-runtime-controls">
    {TEXT_FIT_RUNTIME_FIELDS.map(({ key, label, tooltip }) => (
      <div className="slot-text-runtime-field" key={key}>
        <RuntimeFieldLabel
          id={`${tooltipIdPrefix}-${key}`}
          label={label}
          tooltip={tooltip}
        />
        <Form.Control
          className="slot-config-number"
          type="number"
          min={VIDEO_TEXT_FIT_RUNTIME_LIMITS[key].min}
          max={VIDEO_TEXT_FIT_RUNTIME_LIMITS[key].max}
          aria-label={label}
          value={runtime?.[key] || ''}
          onChange={(event) => onChange(key, event.target.value)}
          disabled={disabled}
        />
      </div>
    ))}
    <div className="slot-text-runtime-field">
      <RuntimeFieldLabel
        id={`${tooltipIdPrefix}-max-length`}
        label={MAX_LENGTH_LABEL}
        tooltip={maxLengthTooltip}
      />
      <Form.Control
        className="slot-config-number"
        type="number"
        min={MIN_POSITIVE_INTEGER}
        aria-label={MAX_LENGTH_LABEL}
        value={runtime?.max_length || ''}
        onChange={(event) => onChange('max_length', event.target.value)}
        disabled={disabled}
      />
    </div>
  </div>
);

const SlotConfigEditor = ({
  disabled,
  fontOptions,
  index,
  onSlotConfigChange,
  onSlotRuntimeChange,
  slot,
}) => {
  if (slot.group_key) {
    return <span className="slot-config-empty">{EMPTY_VALUE_LABEL}</span>;
  }

  if (isMediaSlotKind(slot.kind)) {
    return (
      <Form.Select
        className="slot-config-select"
        value={String(slot.config?.adapt_type ?? VIDEO_PRODUCTION_DEFAULT_ADAPT_TYPE)}
        onChange={(event) =>
          onSlotConfigChange(index, 'adapt_type', event.target.value)
        }
        disabled={disabled}
      >
        {VIDEO_PRODUCTION_ADAPT_TYPE_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Form.Select>
    );
  }

  if (slot.kind === VIDEO_PRODUCTION_SLOT_KIND.TEXT) {
    const selectedFontFile = String(slot.config?.font_file || '');

    return (
      <div className="slot-config-stack">
        <FontSelectControl
          disabled={disabled}
          fontOptions={fontOptions}
          value={selectedFontFile}
          onChange={(event) =>
            onSlotConfigChange(index, 'font_file', event.target.value)
          }
        />
        <TextRuntimeControls
          disabled={disabled}
          maxLengthTooltip={TEXT_SLOT_MAX_LENGTH_TOOLTIP}
          onChange={(key, value) => onSlotRuntimeChange(index, key, value)}
          runtime={slot.runtime}
          tooltipIdPrefix={`slot-${slot.key}`}
        />
      </div>
    );
  }

  return <span className="slot-config-empty">{EMPTY_VALUE_LABEL}</span>;
};

export const SortableSlotRow = ({
  disabled,
  fontOptions,
  insideGroup = false,
  index,
  selected = false,
  onSlotSelectChange,
  onSlotConfigChange,
  onSlotRuntimeChange,
  slot,
  sortableId,
  onSlotChange,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    style,
    isDragging,
  } = useVerticalSortableItem({
    id: sortableId,
    disabled,
  });

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={joinClassNames(
        'slot-editor-row',
        'vertical-sortable-item',
        isDragging && 'is-sortable-dragging',
        insideGroup && 'is-inside-group'
      )}
    >
      <td className="slot-select-cell">
        {insideGroup ? (
          <span className="slot-group-child-marker" />
        ) : (
          <Form.Check
            checked={selected}
            onChange={(event) =>
              onSlotSelectChange(slot.key, event.target.checked)
            }
            disabled={disabled}
            aria-label={`${SLOT_SELECT_ARIA_PREFIX} ${slot.key}`}
          />
        )}
      </td>
      <td>
        <span className="slot-key">{slot.key || EMPTY_VALUE_LABEL}</span>
      </td>
      <td>
        {slot.kind === VIDEO_PRODUCTION_SLOT_KIND.TEXT ? (
          <Form.Control
            value={slot.default_content || ''}
            onChange={(event) =>
              onSlotChange(index, 'default_content', event.target.value)
            }
            disabled={disabled}
            placeholder="默认文字"
          />
        ) : (
          <span className="slot-config-empty">{EMPTY_VALUE_LABEL}</span>
        )}
      </td>
      <td className="slot-config-cell">
        <SlotConfigEditor
          disabled={disabled}
          fontOptions={fontOptions}
          index={index}
          onSlotConfigChange={onSlotConfigChange}
          onSlotRuntimeChange={onSlotRuntimeChange}
          slot={slot}
        />
      </td>
      <td className="slot-sort-cell">
        <SortableDragHandle
          attributes={attributes}
          disabled={disabled}
          className="slot-drag-handle"
          label={`${SLOT_SORT_ARIA_PREFIX} ${slot.key || index + 1}`}
          listeners={listeners}
        />
      </td>
    </tr>
  );
};

const SlotGroupConfigControls = ({
  disabled,
  fontOptions,
  group,
  groupKey,
  onConfigChange,
  onRuntimeChange,
}) => {
  const kind = group.kind || VIDEO_PRODUCTION_SLOT_KIND.IMAGE;
  const selectedFontFile = String(group.config?.font_file || '');

  if (isMediaSlotKind(kind)) {
    return (
      <div className="slot-group-config-controls is-media">
        <Form.Select
          value={String(group.config?.adapt_type ?? VIDEO_PRODUCTION_DEFAULT_ADAPT_TYPE)}
          onChange={(event) =>
            onConfigChange(groupKey, 'adapt_type', event.target.value)
          }
          disabled={disabled}
        >
          {VIDEO_PRODUCTION_ADAPT_TYPE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      </div>
    );
  }

  return (
    <div className="slot-group-config-controls is-text">
      <FontSelectControl
        disabled={disabled}
        fontOptions={fontOptions}
        value={selectedFontFile}
        onChange={(event) =>
          onConfigChange(groupKey, 'font_file', event.target.value)
        }
      />
      <TextRuntimeControls
        disabled={disabled}
        maxLengthTooltip={TEXT_GROUP_MAX_LENGTH_TOOLTIP}
        onChange={(key, value) => onRuntimeChange(groupKey, key, value)}
        runtime={group.runtime}
        tooltipIdPrefix={`group-${groupKey}`}
      />
    </div>
  );
};

export const SortableGroupRow = ({
  disabled,
  expanded,
  fontOptions,
  group,
  groupKey,
  selected = false,
  onConfigChange,
  onFieldChange,
  onGroupSelectChange,
  onRuntimeChange,
  onToggleExpanded,
  slotCount,
  sortableId,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    style,
    isDragging,
  } = useVerticalSortableItem({
    id: sortableId,
    disabled,
  });
  const kind = group.kind || VIDEO_PRODUCTION_SLOT_KIND.IMAGE;
  const groupLabel = group.name || `${getTemplateSlotKindLabel(kind)}组`;

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={joinClassNames(
        'slot-group-row',
        'vertical-sortable-item',
        isDragging && 'is-sortable-dragging'
      )}
    >
      <td className="slot-select-cell">
        <Form.Check
          checked={selected}
          onChange={(event) =>
            onGroupSelectChange(groupKey, event.target.checked)
          }
          disabled={disabled}
          aria-label={`${GROUP_SELECT_ARIA_PREFIX} ${groupLabel}`}
        />
      </td>
      <td>
        <div className="slot-group-row__name-cell">
          <Form.Control
            className="slot-group-row__name-input"
            value={group.name || ''}
            onChange={(event) =>
              onFieldChange(groupKey, 'name', event.target.value)
            }
            disabled={disabled}
            placeholder={GROUP_NAME_PLACEHOLDER}
          />
          <Button
            type="button"
            variant="link"
            className="slot-group-row__count-button"
            onClick={() => onToggleExpanded(groupKey)}
            disabled={disabled}
            aria-expanded={expanded}
            aria-label={`${slotCount} ${SLOT_COUNT_ARIA_SUFFIX}`}
          >
            {slotCount}
          </Button>
        </div>
      </td>
      <td>
        <span className="slot-config-empty">{EMPTY_VALUE_LABEL}</span>
      </td>
      <td className="slot-config-cell">
        <SlotGroupConfigControls
          disabled={disabled}
          fontOptions={fontOptions}
          group={group}
          groupKey={groupKey}
          onConfigChange={onConfigChange}
          onRuntimeChange={onRuntimeChange}
        />
      </td>
      <td className="slot-sort-cell">
        <SortableDragHandle
          attributes={attributes}
          disabled={disabled}
          className="slot-drag-handle"
          label={`${GROUP_SORT_ARIA_PREFIX} ${groupLabel}`}
          listeners={listeners}
        />
      </td>
    </tr>
  );
};
